import { isEmpty, mergeDeepLeft, omit, reverse } from 'ramda';
import type { Location } from 'slate';
import { createEditor, Editor, Element, Node, Path, Point, Range, Text, Transforms } from 'slate';

import TabTransforms from './tab-transforms';
import type { UUID } from '../../types/type-helpers';
import type {
  AlineEditor,
  AlineText,
  DeleteParagraphEdit,
  DocParts,
  InsertParagraphEdit,
  PageBreakElement,
  ParagraphElement,
  PartialParagraphProps,
  SuggestedParagraphGlyphEdit,
  SuggestionSource,
} from '../internal';
import {
  AlineTransforms,
  breakType,
  BreakVariant,
  composePlugins,
  createDefaultParagraph,
  EditingMode,
  isCheckboxElement,
  isDocumentElement,
  isDrawingElement,
  isInlineInstructionFieldElement,
  isListItem,
  isParagraphElement,
  isParagraphEntry,
  isTabElement,
  isTableCellElement,
  isTableElement,
  isTableRowElement,
  maxListLevel,
  Queries,
  toParagraphNodeProps,
  withApply,
  withCorePlugins,
  withHeaderFooterCacheInvalidation,
  withValidation,
} from '../internal';

const GeneralTransforms = {
  /** Announce a message to the screen reader if available. */
  announce(editor: Editor, message: string) {
    editor.announce?.(message);
  },

  /**
   * Creates an Editor that is only suitable for querying values from the JSON.
   */
  createQueryEditorFromJson: (docParts: DocParts): AlineEditor => {
    return AlineTransforms.createServerEditorFromJson(docParts, {
      state: {
        editingUserId: null,
        isInSigningCeremony: false,
        isReadOnly: true,
        isSigned: false,
        userDeclinedSigning: false,
      },
    });
  },

  /**
   * Create an Editor object for the Server.  Main difference between this and createClientEditor is createClientEditor *includes withReact*.
   *
   * NOTE: The order of plugins matters.
   * withValidation should be last so it's editor.normalizeNode is called first and the node(s) is validated before other funcs are called.
   * withReadOnly should also be ahead of withApply since it modifies editor.apply and short circuits calling apply if the editor is "read only".
   * In short, pay attention to plugin order if you modify this initialization function.
   */
  createServerEditor: (
    state: NonNullable<AlineEditor['userState']>,
    overrides?: Partial<Editor>
  ): Editor => {
    const editorType: AlineEditor['type'] = 'AlineEditor';
    const withPlugins = composePlugins(
      withApply,
      withCorePlugins,
      // Halt normalizing here and don't call Slate's default normalization.
      e => withHeaderFooterCacheInvalidation(e, { callNextNormalization: false }),
      withValidation
    );
    const editor = withPlugins(createEditor());

    return Object.assign(editor, {
      type: editorType,
      userState: state,
      ...overrides,
    } satisfies Partial<Editor>);
  },

  createServerEditorFromJson: (
    docParts: DocParts,
    options: { state: NonNullable<AlineEditor['userState']>; overrides?: Partial<Editor> }
  ): AlineEditor => {
    const { overrides, state } = options;
    const editor = AlineTransforms.createServerEditor(state, overrides);
    editor.children = docParts;

    return editor as AlineEditor;
  },

  decreaseIndentation: (
    editor: Editor,
    options: { authorId: string; editingMode: EditingMode }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editingMode } = options;

      for (const [paragraph, path] of Queries.paragraphsAtLocation(editor, { listMarker: true })) {
        if (isListItem(paragraph)) {
          const safeLiseLevel = Queries.renderedParagraphListLevel(paragraph) ?? 0;
          // We only unindent the current paragraph, if we are not at the top-level list.
          if (safeLiseLevel > 0) {
            AlineTransforms.indentParagraph(editor, {
              at: path,
              authorId,
              direction: 'decrease',
              editingMode,
            });
            AlineTransforms.updateListLevel(editor, {
              at: path,
              authorId,
              direction: 'decrease',
              editingMode,
            });
          }
        } else {
          AlineTransforms.indentParagraph(editor, {
            at: path,
            authorId,
            direction: 'decrease',
            editingMode,
          });
        }
      }
    });
  },

  increaseIndentation: (
    editor: Editor,
    options: { authorId: string; editingMode: EditingMode }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editingMode } = options;

      for (const [paragraph, path] of Queries.paragraphsAtLocation(editor, { listMarker: true })) {
        const safeListLevel = Queries.renderedParagraphListLevel(paragraph) ?? 0;
        if (isListItem(paragraph) && safeListLevel >= maxListLevel) continue;

        const indentationError = AlineTransforms.indentParagraph(editor, {
          at: path,
          authorId,
          direction: 'increase',
          editingMode,
        });
        if (!indentationError && isListItem(paragraph)) {
          AlineTransforms.updateListLevel(editor, {
            at: path,
            authorId,
            direction: 'increase',
            editingMode,
          });
        }
      }
    });
  },

  /**
   * Insert a break at a Location, taking into account editing mode.
   *
   * TODO: If changing this method, consider adding calls to AlineTransforms.announce to improve
   * screen reader support as per EDI-393.
   */
  insertBreak: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      at?: Location;
      select?: boolean;
      source?: SuggestionSource;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode, select = true, source } = options;
      if (!at) return;

      const atRange = Editor.range(editor, at);

      switch (editingMode) {
        case EditingMode.COMMENTING:
        case EditingMode.VIEWING:
          return;
        case EditingMode.EDITING: {
          const [start] = Range.edges(atRange);
          const startRef = Editor.pointRef(editor, start);

          /**
           * Wipe out expanded selection.
           */
          if (Range.isExpanded(atRange))
            AlineTransforms.deleteContent(editor, { at: atRange, authorId, editingMode });

          const startAfterDelete = startRef.unref();
          // Range must be collapsed since we deleted all content if Range was expanded.
          if (!startAfterDelete) return;

          const isInserted = AlineTransforms.insertBreakInCheckboxOptionLabel(editor, {
            at: startAfterDelete,
            authorId,
            editingMode,
            select,
          });
          if (isInserted) return;

          const paraEntryPriorToSplit = Queries.paragraphAtPoint(editor, {
            at: startAfterDelete,
          });
          if (!paraEntryPriorToSplit) return;

          /**
           * If we're inside a list item that is empty and the user hits enter, we want to
           * convert the list item to a regular paragraph so users can have paragraphs between list items.
           */
          const [paraPriorToSplit, paraPathPriorToSplit] = paraEntryPriorToSplit;
          if (isListItem(paraPriorToSplit) && Queries.nodeIsEmpty(paraPriorToSplit)) {
            AlineTransforms.setParagraphProps(
              editor,
              editingMode,
              authorId,
              { indentationFirstLine: 0, indentationLeft: 0, listProps: null },
              { at: paraPathPriorToSplit }
            );

            if (select) AlineTransforms.select(editor, Editor.start(editor, paraPathPriorToSplit));
            return;
          }

          AlineTransforms.splitParagraph(editor, { at: startAfterDelete });

          const nextPath = Path.next(paraPathPriorToSplit);
          const paragraphEntryAfterSplit = Node.has(editor, nextPath)
            ? Editor.node(editor, nextPath)
            : null;
          if (!paragraphEntryAfterSplit || !isParagraphEntry(paragraphEntryAfterSplit)) return;

          /**
           * If we're not in suggestion mode, don't carry over the suggested edits from the paragraph being split.
           * Instead, apply them to the split paragraph as if the edits were accepted.
           */
          const [paraAfterSplit, paraPathAfterSplit] = paragraphEntryAfterSplit;
          if (paraAfterSplit.suggestedEdits && !isEmpty(paraAfterSplit.suggestedEdits)) {
            const splitParaProps = reverse(paraAfterSplit.suggestedEdits).reduce(
              (splitProps: Partial<PartialParagraphProps>, suggestedParagraphEdit) =>
                mergeDeepLeft(suggestedParagraphEdit.value, splitProps),
              {}
            );
            Transforms.setNodes(
              editor,
              { ...toParagraphNodeProps(splitParaProps), suggestedEdits: [] },
              { match: isParagraphElement }
            );
          }

          /**
           * The original pilcrow (and any glyph suggestion on it) belongs to the
           * lower half; the upper half gets a brand-new pilcrow. Slate copies
           * `glyphProps` onto both halves, so clear the inherited paragraph-mark
           * suggestions from the upper half (suggesting mode overwrites them instead).
           */
          const upperHalf = Node.get(editor, paraPathPriorToSplit);
          if (isParagraphElement(upperHalf) && !isEmpty(upperHalf.glyphProps?.suggestedEdits ?? []))
            setGlyphSuggestedEdits(editor, paraPathPriorToSplit, []);

          if (select) AlineTransforms.select(editor, Editor.start(editor, paraPathAfterSplit));

          return;
        }
        case EditingMode.SUGGESTING: {
          const [start] = Range.edges(atRange);
          const startRef = Editor.pointRef(editor, start);
          if (Range.isExpanded(atRange))
            AlineTransforms.deleteContent(editor, { at: atRange, authorId, editingMode });

          const startAfterDelete = startRef.unref();
          if (!startAfterDelete) return;

          const isInserted = AlineTransforms.insertBreakInCheckboxOptionLabel(editor, {
            at: startAfterDelete,
            authorId,
            editingMode,
            select,
          });
          if (isInserted) return;

          const paraEntryPriorToSplit = Queries.paragraphAtPoint(editor, {
            at: startAfterDelete,
          });
          if (!paraEntryPriorToSplit) return;

          AlineTransforms.suggestBreakAtCursor(editor, {
            authorId,
            cursorPoint: startAfterDelete,
            editingMode,
            source,
          });

          const [, paraPathPriorToSplit] = paraEntryPriorToSplit;
          const nextPath = Path.next(paraPathPriorToSplit);
          const paragraphEntryAfterSplit = Node.has(editor, nextPath)
            ? Editor.node(editor, nextPath)
            : null;
          if (!paragraphEntryAfterSplit || !isParagraphEntry(paragraphEntryAfterSplit)) return;

          const [, paraPathAfterSplit] = paragraphEntryAfterSplit;
          if (select) AlineTransforms.select(editor, Editor.start(editor, paraPathAfterSplit));
        }
      }
    });
  },

  /**
   * Insert a page break at the selection.
   */
  insertPageBreak: (
    editor: Editor,
    options: { authorId: string; editingMode: EditingMode }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editingMode } = options;
      if (!editor.selection) return;

      // Don't permit page breaks: (1) Inside headers/footers or any "non-document" node, or (2) Within tables.
      const selBoundary = Queries.selectionBoundaryAboveLoc(editor);
      const tableCellEntry = Editor.above(editor, { match: isTableCellElement });
      if (tableCellEntry || !selBoundary || !isDocumentElement(selBoundary[0])) return;

      const paragraphEntryAtFocus = Queries.paragraphAtPoint(editor);
      if (!paragraphEntryAtFocus) return;

      const [, focusParaPath] = paragraphEntryAtFocus;
      const pathRef = Editor.pathRef(editor, focusParaPath);

      AlineTransforms.insertBreak(editor, { authorId, editingMode });

      const pathAfterSplit = pathRef.unref();
      if (!pathAfterSplit) return;

      const isSuggesting = editingMode === EditingMode.SUGGESTING;
      const styleSheet = Queries.activeStyleSheet(editor);
      const norm = styleSheet['style-normal'].styles;
      function createNormText(includeInsertEdit = true): AlineText {
        return {
          ...norm,
          comments: [],
          suggestedEdits:
            isSuggesting && includeInsertEdit
              ? [
                  {
                    authorId,
                    createdAt: new Date().toISOString(),
                    type: 'insertNode',
                    updatedAt: new Date().toISOString(),
                    value: {},
                  },
                ]
              : [],
          text: '',
        };
      }

      const pageBreakElement: PageBreakElement = {
        children: [createNormText(false)],
        suggestedEdits: isSuggesting
          ? [
              {
                authorId,
                createdAt: new Date().toISOString(),
                type: 'insertBreak',
                updatedAt: new Date().toISOString(),
                value: {},
                variant: BreakVariant.Page,
              },
            ]
          : [],
        type: breakType,
        variant: BreakVariant.Page,
      };

      const newParagraph: ParagraphElement = {
        ...createDefaultParagraph(),
        children: [createNormText(), pageBreakElement, createNormText()],
      };

      Transforms.insertNodes(editor, newParagraph, { at: pathAfterSplit, select: false });
    });
  },

  /**
   * Allows you to split text at the edges of a Location.  Keep in mind if the Location is a collapsed range,
   * only a single split at the Point of the range occurs.
   */
  splitTextAtEdges: (
    editor: Editor,
    options: { editingMode: EditingMode; at?: Location; hanging?: boolean; voids?: boolean }
  ): Range | null => {
    let { at = editor.selection } = options;
    const { hanging = false, voids = false } = options;
    if (!at) return null;

    // If it's a Range, unhang it first so we avoid weird oddities/bugs when the anchor/focus are at the start/end of their respective text nodes.
    if (Range.isRange(at) && !hanging) {
      const unhungRange = AlineTransforms.unhangRange(editor, { at, voids });
      if (!unhungRange) return null;
      at = unhungRange;
    }

    const atRange = Editor.range(editor, at);
    const rangeCollapsed = Range.isCollapsed(atRange);
    const rangeRef = Editor.rangeRef(editor, atRange, {
      affinity: rangeCollapsed ? 'forward' : 'inward',
    });

    AlineTransforms.withoutNormalizing(editor, () => {
      const shouldSkipTextSplit = (point: Point) =>
        Boolean(
          Editor.above(editor, {
            at: point,
            match: node =>
              isCheckboxElement(node) ||
              isDrawingElement(node) ||
              isTabElement(node) ||
              isInlineInstructionFieldElement(node),
            voids: true,
          })
        );

      if (rangeCollapsed) {
        if (!shouldSkipTextSplit(atRange.anchor)) {
          Transforms.splitNodes(editor, { at: atRange.anchor, match: Text.isText, voids });
        }

        return;
      }

      const [startPoint, endPoint] = Range.edges(atRange);
      const startPointAtStartOfNode = Editor.isStart(editor, startPoint, startPoint.path);
      const endPointAtEndOfNode = Editor.isEnd(editor, endPoint, endPoint.path);

      /**
       * IMPORTANT: Split the end point first so we don't affect the path of the
       * start point by splitting the end.
       */
      if (!shouldSkipTextSplit(endPoint)) {
        Transforms.splitNodes(editor, {
          always: !endPointAtEndOfNode,
          at: endPoint,
          match: Text.isText,
          voids,
        });
      }
      if (!shouldSkipTextSplit(startPoint)) {
        Transforms.splitNodes(editor, {
          always: !startPointAtStartOfNode,
          at: startPoint,
          match: Text.isText,
          voids,
        });
      }
    });

    return rangeRef.unref();
  },

  /**
   * Suggest a paragraph break at the cursor. The paragraph above the new break
   * (i.e. the first half of the split) gets an `insertParagraph` edit on its
   * `glyphProps.suggestedEdits` — mirroring MS Word's `pPr/rPr/ins` model.
   */
  suggestBreakAtCursor: (
    editor: Editor,
    options: {
      authorId: string;
      cursorPoint: Point;
      editingMode: EditingMode;
      source?: SuggestionSource;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, cursorPoint, source } = options;

      const paragraphEntry = Editor.above<ParagraphElement>(editor, {
        at: cursorPoint,
        match: isParagraphElement,
      });

      if (!paragraphEntry) return;

      const [, paragraphPath] = paragraphEntry;

      AlineTransforms.splitParagraph(editor, { at: cursorPoint });
      markParagraphAsSuggestedInsert(editor, paragraphPath, { authorId, source });
    });
  },

  /**
   * Suggest a paragraph merge at the cursor (Backspace at start of paragraph N
   * or Delete at end of paragraph N-1). The "absorbing" paragraph (the one
   * above the boundary, P_prev) gets a `deleteParagraph` edit on its
   * `glyphProps.suggestedEdits` — mirroring MS Word's `pPr/rPr/del` model.
   *
   * The two paragraphs are NOT actually merged in suggesting mode — they stay
   * visually distinct until the edit is accepted.
   */
  suggestMergeAtCursor: (
    editor: Editor,
    options: {
      authorId: string;
      cursorPoint: Point;
      direction: 'backward' | 'forward';
      editingMode: EditingMode;
      select?: boolean;
      source?: SuggestionSource;
    }
  ): boolean => {
    return AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, cursorPoint, direction, select = true, source } = options;

      const paragraphEntry = Editor.above<ParagraphElement>(editor, {
        at: cursorPoint,
        match: isParagraphElement,
      });
      if (!paragraphEntry) return false;

      const [, paragraphPath] = paragraphEntry;

      /**
       * Direction backward: cursor at start of P_N — the pilcrow being deleted belongs to P_{N-1}.
       * Direction forward: cursor at end of P_N — the pilcrow being deleted belongs to P_N.
       */
      const targetParagraphPath =
        direction === 'backward'
          ? Path.hasPrevious(paragraphPath)
            ? Path.previous(paragraphPath)
            : null
          : paragraphPath;
      if (!targetParagraphPath || !Node.has(editor, targetParagraphPath)) return false;

      const targetNode = Node.get(editor, targetParagraphPath);
      if (!isParagraphElement(targetNode)) return false;

      // For forward direction, ensure there's a paragraph after the target to merge with.
      const followingPath = Path.next(targetParagraphPath);
      if (!Node.has(editor, followingPath)) return false;
      const followingNode = Node.get(editor, followingPath);
      if (!isParagraphElement(followingNode)) return false;

      /**
       * If the target paragraph carries the author's own `insertParagraph` edit,
       * the user is deleting a paragraph break they themselves just suggested.
       * Don't stack a `deleteParagraph` on top — instead treat it like reject:
       * strip the insertion and re-merge the paragraphs. The two cancel out and
       * the document returns to its pre-Enter state for that boundary. Matches
       * Word / Docs: typing-then-deleting in the same suggesting session is a
       * net no-op, not two opposing tracked edits.
       */
      const ownInsertEdit = targetNode.glyphProps?.suggestedEdits?.find(
        (edit): edit is InsertParagraphEdit =>
          edit.type === 'insertParagraph' && edit.authorId === authorId
      );
      if (ownInsertEdit) {
        AlineTransforms.rejectSuggestedInsertParagraph(editor, {
          insertParagraphEditEntry: {
            edit: ownInsertEdit,
            range: Editor.range(editor, targetParagraphPath),
          },
        });
        // Slate's mergeNodes (invoked inside reject) keeps the selection at the
        // correct merge point automatically — no explicit move needed.
        return true;
      }

      markParagraphAsSuggestedDelete(editor, targetParagraphPath, { authorId, source });

      /**
       * Move the cursor past the absorbed boundary so consecutive Backspaces /
       * Deletes can cascade through paragraphs (matching Word / Docs). Without
       * this, the cursor sits on the same boundary and the next press finds
       * the edit `alreadySuggested` and no-ops.
       */
      if (select) {
        const nextSelection =
          direction === 'backward'
            ? Editor.end(editor, targetParagraphPath)
            : Editor.start(editor, followingPath);
        AlineTransforms.select(editor, nextSelection);
      }

      return true;
    });
  },

  /**
   * Transform for when the user hits the tab key.
   */
  tab: (
    editor: Editor,
    options: { authorId: UUID; editingMode: EditingMode; direction?: 'forward' | 'backward' }
  ): void => {
    const { authorId, direction = 'forward', editingMode } = options;
    if (!editor.selection) return;

    const indentTransform =
      direction === 'forward'
        ? AlineTransforms.increaseIndentation
        : AlineTransforms.decreaseIndentation;
    const tableCellEntry = Editor.above(editor, {
      at: editor.selection.focus,
      match: isTableCellElement,
    });
    /**
     * If we're not in a table, then insert a tab element here. Note that we
     * insert a tab element regardless of whether it is forward or backwards.
     * That is, if the shift key is pressed, we still insert a tab element.
     * This is consistent with the way Microsoft Word works.
     */
    if (!tableCellEntry) {
      if (Range.isExpanded(editor.selection))
        return TabTransforms.insertTabElement(editor, options);

      const paragraphEntry = Editor.above(editor, {
        at: editor.selection.focus,
        match: isParagraphElement,
      });
      if (!paragraphEntry) throw new Error('Selection is not inside a paragraph.');

      const [paragraph, paragraphPath] = paragraphEntry;
      const isParagraphEmpty = Queries.nodeIsEmpty(paragraph);
      const paragraphStartPoint = Editor.start(editor, paragraphPath);
      const isAtStartOfLine = Point.equals(editor.selection.focus, paragraphStartPoint);
      const isAtStartOfListItem = isAtStartOfLine && isListItem(paragraph);
      const isAtStartOfRegParaWithContent = isAtStartOfLine && !isParagraphEmpty;

      if (isAtStartOfListItem || isAtStartOfRegParaWithContent)
        return indentTransform(editor, options);

      return TabTransforms.insertTabElement(editor, options);
    }

    const [, tableCellPath] = tableCellEntry;
    const tableEntry = Editor.above(editor, { at: tableCellPath, match: isTableElement });
    const tableRowEntry = Editor.above(editor, { at: tableCellPath, match: isTableRowElement });
    if (!tableEntry || !tableRowEntry)
      throw new Error(`Table cell at path ${JSON.stringify(tableCellPath)} is not inside a table.`);

    const [table] = tableEntry;
    const [tableRow, tableRowPath] = tableRowEntry;
    const rowCount = table.children.length;
    const rowNumber = tableRowPath[tableRowPath.length - 1] + 1;
    const rowCellCount = tableRow.children.length;
    const cellNumber = tableCellPath[tableCellPath.length - 1] + 1;

    switch (direction) {
      case 'forward': {
        const isLastRow = rowNumber === rowCount;
        const isLastCellInRow = cellNumber === rowCellCount;
        if (!isLastCellInRow)
          return AlineTransforms.select(editor, Editor.start(editor, Path.next(tableCellPath)));
        if (!isLastRow)
          return AlineTransforms.select(editor, Editor.start(editor, Path.next(tableRowPath)));

        AlineTransforms.insertTableRows(editor, {
          at: tableRowPath,
          authorId,
          editingMode,
          rowLocation: 'below',
        });

        // Select the start of the newly-inserted row.
        return AlineTransforms.select(editor, Editor.start(editor, Path.next(tableRowPath)));
      }
      case 'backward': {
        const isFirstRow = rowNumber === 1;
        const isFirstCellInRow = cellNumber === 1;
        if (!isFirstCellInRow) return AlineTransforms.select(editor, Path.previous(tableCellPath));
        if (!isFirstRow) {
          const [prevRow, prevRowPath] = Editor.node(editor, Path.previous(tableRowPath));
          if (!isTableRowElement(prevRow)) return;
          const lastCellPath = [...prevRowPath, prevRow.children.length - 1];

          return AlineTransforms.select(editor, lastCellPath);
        }

        // If it is the first cell in the first row, do nothing on purpose.
      }
    }
  },

  /**
   * Unwrap inlines at a Location (e.g., so that inlines don't wrap other inlines).
   */
  unwrapInlines: (
    editor: Editor,
    options: { editingMode: EditingMode; at?: Location; voids?: boolean }
  ): Location | null => {
    const { at = editor.selection, voids = false } = options;
    if (!at) return null;

    const locationHasInlines = Queries.locationHasInlines(editor, { at, voids });
    if (!locationHasInlines) return at;

    const ref = Range.isRange(at)
      ? Editor.rangeRef(editor, at, { affinity: 'inward' })
      : Path.isPath(at)
        ? Editor.pathRef(editor, at)
        : Editor.pointRef(editor, at);

    AlineTransforms.withoutNormalizing(editor, () => {
      Transforms.unwrapNodes(editor, {
        at,
        match: node => Element.isElement(node) && Editor.isInline(editor, node),
        split: true,
        voids,
      });
    });

    return ref.unref();
  },

  /**
   * The same as Slate's Editor.withoutNormalizing, but uses the function's return value.
   */
  withoutNormalizing: <T>(editor: Editor, fn: () => T): T => {
    const wasNormalizing = Editor.isNormalizing(editor);
    Editor.setNormalizing(editor, false);
    let result: T;
    try {
      result = fn();
    } finally {
      Editor.setNormalizing(editor, wasNormalizing);
    }
    Editor.normalize(editor);
    return result;
  },
};

/**
 * Sets `glyphProps.suggestedEdits` on a paragraph, preserving the rest of
 * `glyphProps`. Passing an empty array drops `suggestedEdits` entirely, and
 * collapses `glyphProps` to `undefined` when nothing else remains.
 */
export function setGlyphSuggestedEdits(
  editor: Editor,
  paragraphPath: Path,
  suggestedEdits: SuggestedParagraphGlyphEdit[]
): void {
  const node = Node.get(editor, paragraphPath);
  if (!isParagraphElement(node)) return;

  const restGlyphProps: Omit<NonNullable<ParagraphElement['glyphProps']>, 'suggestedEdits'> = omit(
    ['suggestedEdits'],
    node.glyphProps ?? {}
  );

  let nextGlyphProps: ParagraphElement['glyphProps'];
  if (!isEmpty(suggestedEdits)) {
    nextGlyphProps = { ...restGlyphProps, suggestedEdits };
  } else if (!isEmpty(restGlyphProps)) {
    nextGlyphProps = restGlyphProps;
  } else {
    nextGlyphProps = undefined;
  }

  Transforms.setNodes<ParagraphElement>(
    editor,
    { glyphProps: nextGlyphProps },
    { at: paragraphPath }
  );
}

/**
 * When two paragraphs merge, the upper paragraph's node survives but its
 * pilcrow does not — the surviving pilcrow is the lower paragraph's. Copy the
 * lower `glyphProps` onto the upper paragraph *before* the merge so the
 * survivor wears the right pilcrow (slate keeps the upper node's props, and if
 * the merge instead removes an emptied upper paragraph, the lower one already
 * has its own). Glyph suggestions still on the upper pilcrow are dropped: they
 * describe a paragraph boundary that no longer exists.
 */
export function transferGlyphPropsForParagraphMerge(
  editor: Editor,
  options: { lowerPath: Path; upperPath: Path }
): void {
  const { lowerPath, upperPath } = options;
  const upperNode = Node.get(editor, upperPath);
  const lowerNode = Node.get(editor, lowerPath);
  if (!isParagraphElement(upperNode) || !isParagraphElement(lowerNode)) return;

  Transforms.setNodes<ParagraphElement>(
    editor,
    { glyphProps: lowerNode.glyphProps },
    { at: upperPath }
  );
}

/**
 * Marks a paragraph's trailing pilcrow as a suggested deletion by appending a
 * `deleteParagraph` glyph edit, preserving other pending glyph edits (e.g.
 * another author's `insertParagraph`, which the delete layers on top of).
 * No-ops when the paragraph already carries a same-author `deleteParagraph`.
 */
export function markParagraphAsSuggestedDelete(
  editor: Editor,
  paragraphPath: Path,
  options: { authorId: string; groupId?: string; source?: SuggestionSource }
): void {
  const { authorId, groupId, source } = options;
  const node = Node.get(editor, paragraphPath);
  if (!isParagraphElement(node)) return;

  const existingEdits = node.glyphProps?.suggestedEdits ?? [];
  const alreadySuggested = existingEdits.some(
    edit => edit.type === 'deleteParagraph' && edit.authorId === authorId
  );
  if (alreadySuggested) return;

  const deleteSuggestion: DeleteParagraphEdit = {
    authorId,
    createdAt: new Date().toISOString(),
    ...(groupId ? { groupId } : {}),
    source,
    type: 'deleteParagraph',
    updatedAt: new Date().toISOString(),
    value: {},
  };
  setGlyphSuggestedEdits(editor, paragraphPath, [...existingEdits, deleteSuggestion]);
}

/**
 * Marks a paragraph as a suggested insertion by writing a single `insertParagraph`
 * glyph edit
 */
export function markParagraphAsSuggestedInsert(
  editor: Editor,
  paragraphPath: Path,
  options: { authorId: string; groupId?: string; source?: SuggestionSource }
): void {
  const { authorId, groupId, source } = options;
  const insertSuggestion: InsertParagraphEdit = {
    authorId,
    createdAt: new Date().toISOString(),
    ...(groupId ? { groupId } : {}),
    source,
    type: 'insertParagraph',
    updatedAt: new Date().toISOString(),
    value: {},
  };
  setGlyphSuggestedEdits(editor, paragraphPath, [insertSuggestion]);
}

export default GeneralTransforms;
